import React from 'react';
import { useIntl } from '@atlassian/jira-intl';
import { Tooltip } from '@atlassian/jira-portfolio-3-common';
import type { OptionType } from '@atlassian/jira-portfolio-3-common/src/select/types';
import { PRODUCT_ANALYTICS_EVENT_NAMES } from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/analytics/types';
import BasicPopupSelect from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/popup-select/basic';
import {
	GroupBy,
	ROLLUP_BY_OPTION,
} from '@atlassian/jira-portfolio-3-portfolio/src/app-simple-plans/view/main/tabs/dependencies/types';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
import * as styles from './styles.module.css';
import type { Props } from './types';

function RollupFilter({
	hierarchy,
	rollupBy,
	groupBy,
	rolledUpHierarchyLevel,
	isOpen = false,
	setRollUp,
	onFilterChange,
}: Props) {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const noneItem = {
		label: intl.formatMessage(messages.rollUpByNone),
		value: ROLLUP_BY_OPTION.NONE,
	};

	const isTeamDisabled =
		groupBy !== null && groupBy !== GroupBy.EPIC_LEVEL && groupBy !== GroupBy.NONE;

	const teamLabel = intl.formatMessage(messages.rollUpByTeam);
	const teamItem = {
		label: isTeamDisabled ? (
			<Tooltip content={intl.formatMessage(messages.restrictedRollupTeamWithGroupBy)}>
				{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766 */}
				<div className={styles.disabledTeam}>{teamLabel}</div>
			</Tooltip>
		) : (
			teamLabel
		),
		value: ROLLUP_BY_OPTION.TEAM,
		isDisabled: isTeamDisabled,
	};

	let selectedValue: OptionType | undefined = noneItem;

	if (rollupBy === ROLLUP_BY_OPTION.TEAM) {
		selectedValue = teamItem;
	} else if (rollupBy === ROLLUP_BY_OPTION.HIERARCHY) {
		selectedValue = hierarchy.find(({ value }) => value === rolledUpHierarchyLevel);
	}

	const isDisabled = groupBy === GroupBy.EPIC_LEVEL;

	const options = [
		noneItem,
		{
			label: intl.formatMessage(messages.hierarchyRollupHeading),
			options: hierarchy,
		},
		{
			label: intl.formatMessage(messages.otherRollupHeading),
			options: [teamItem],
		},
	];

	const select = (
		<BasicPopupSelect
			label={intl.formatMessage(messages.baseRollupLabel)}
			defaultIsOpen={isOpen}
			options={options}
			emptyValues={[GroupBy.NONE]}
			onChange={(e) => {
				let newRollup;
				let newRolledUpHierarchyLevel;

				if (e.value === ROLLUP_BY_OPTION.NONE || e.value === ROLLUP_BY_OPTION.TEAM) {
					newRollup = e.value.toString();
					newRolledUpHierarchyLevel = -1;
				} else {
					newRollup = ROLLUP_BY_OPTION.HIERARCHY;
					newRolledUpHierarchyLevel = Number(e.value);
				}
				setRollUp(newRollup, newRolledUpHierarchyLevel);

				const [actionSubject, action] =
					PRODUCT_ANALYTICS_EVENT_NAMES.UPDATED_DEPENDENCY_ROLLUP_DROPDOWN.split(' ');
				fireUIAnalytics(
					createAnalyticsEvent({
						action,
						actionSubject,
					}),
					{
						rollupBy: newRollup,
						rollupHierarchyLevel:
							newRollup === ROLLUP_BY_OPTION.HIERARCHY ? newRolledUpHierarchyLevel : null,
					},
				);
				onFilterChange();
			}}
			isDisabled={isDisabled}
			value={selectedValue}
			testId="portfolio-3-portfolio.app-simple-plans.main.tabs.dependencies.filters.rollup"
		/>
	);

	if (isDisabled) {
		return (
			<Tooltip content={intl.formatMessage(messages.restrictedRollupWithGroupByEpic)}>
				{select}
			</Tooltip>
		);
	}

	return select;
}

export default RollupFilter;
